import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Controller} from 'react-hook-form';
import get from 'lodash/get';
import moment from 'moment';
import 'moment/locale/pt-br';
import MomentUtils from '@date-io/moment';
import {KeyboardDatePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import {makeStyles} from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormGroup from '@material-ui/core/FormGroup';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Checkbox from '@material-ui/core/Checkbox';
import {SearchOutlined} from '@material-ui/icons';
import LabelValue from '../../../shared/labelvalue/LabelValue.js';
import GenericTab from '../../../shared/generictab/GenericTab.js';
import Separator from '../../../shared/separator/Separator.js';
import {
    CelularNumberFormatCustom,
    CepNumberFormatCustom,
    CpfNumberFormatCustom,
    TelefoneNumberFormatCustom
} from '../../../shared/formatters/index.js';
import DialogBuscarEndereco from '../../../publica/Page/inscricao/DialogBuscarEndereco.jsx';
import useFetch from '../../../../hooks/useFetch.js';
import useAlertMessage from '../../../../hooks/useAlertMessage.js';
import {URL_ENDERECO_CEPS} from '../../../../constants/index.js';
import {tituloSecao} from '../../../../utils/styles.js';
import {TIPOS_RESPONSAVEL, exigeDadosResponsavel, formatarCep} from '../../components/inscricao/inscricaoAdministrativaPayload.js';
import {indiceSecaoVisivel} from '../../components/inscricao/inscricaoAdministrativaSchema.js';

const FORMATO_DATA = 'DD/MM/YYYY';

/* Mesmas larguras e títulos da etapa Dados Pessoais da inscrição pública. */
const useStyles = makeStyles(theme => ({
    linhaNomeSocial: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1)
    },
    campoNomeSocial: {
        flex: 1
    },
    habilitarNomeSocial: {
        marginRight: 0
    },
    tabs: {
        width: '100%',
        minWidth: '100px',
        maxWidth: '160px',
        marginRight: theme.spacing(0),
        borderRight: `1px solid ${theme.palette.divider}`,
        '& .MuiTab-root': {
            '&.MuiButtonBase-root.MuiTab-root.MuiTab-textColorPrimary': {
                width: '100%',
                minWidth: '100px',
                maxWidth: '160px'
            }
        }
    },
    sectionTitle: {
        ...tituloSecao(theme),
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2)
    }
}));

const SEXOS = [
    {valor: 'MASCULINO', descricao: 'Masculino'},
    {valor: 'FEMININO', descricao: 'Feminino'},
    {valor: 'NAO_DESEJO_INFORMAR', descricao: 'Não desejo informar'}
];

/* Valor do picker a partir do texto dd/MM/yyyy (inválido continua inválido, sem apagar o que foi digitado). */
const dataParaPicker = valor => (valor ? moment(valor, FORMATO_DATA, true) : null);

const cidadeDoCep = cidade => (cidade && cidade.id ? {
    id: cidade.id,
    nome: cidade.nome || '',
    uf: {uf: cidade.uf?.uf || '', nome: cidade.uf?.nome || ''}
} : null);

/*
 * Etapa "Dados Pessoais": seções Dados Pessoais (CPF, identificação, contato,
 * deficiências), Endereço Residencial e Responsável Legal (dados, endereço e
 * contato), no visual da inscrição pública.
 */
export default function EtapaDadosPessoais(props) {
    const {
        control,
        errors,
        watch,
        setValue,
        secoesComErro,
        secaoFoco,
        cpfTravado,
        cpfResolvido,
        nomeTravado,
        dataNascimentoTravada,
        nomeMaeTravado,
        exibirResponsavel,
        menorDeIdade,
        dataEmancipacaoMaior,
        ultimaAlteracao,
        estadosCivis = [],
        racas = [],
        nacionalidades = [],
        deficiencias = []
    } = props;
    const classes = useStyles();
    const tabRef = useRef();
    const fetch = useFetch();
    const showAlertMessage = useAlertMessage();
    const [alvoPesquisa, setAlvoPesquisa] = useState(null);

    const habilitarNomeSocial = watch('pessoa.habilitarNomeSocial');
    const tipoResponsavel = watch('tipoResponsavel');
    const comResponsavel = exigeDadosResponsavel(tipoResponsavel);

    const erro = nome => get(errors, nome);

    const preencherEndereco = (prefixo, dados) => {
        const opcoes = {shouldDirty: true, shouldValidate: true};
        if (dados.cep) setValue(`${prefixo}.cep`, formatarCep(dados.cep), opcoes);
        if (dados.logradouro) setValue(`${prefixo}.logradouro`, dados.logradouro, opcoes);
        if (dados.bairro) setValue(`${prefixo}.bairro`, dados.bairro, opcoes);
        setValue(`${prefixo}.cidade`, cidadeDoCep(dados.cidade), opcoes);
    };

    /* CEP completo: busca o endereço na base de CEPs (mesmo endpoint da inscrição pública). */
    const buscarCep = prefixo => valor => {
        const cep = String(valor || '').replace(/\D/g, '');
        if (cep.length !== 8) return;
        fetch(`${URL_ENDERECO_CEPS}/${cep}`, null, dados => {
            if (dados) preencherEndereco(prefixo, dados);
        }, () => {
            showAlertMessage('O CEP informado não consta na base de dados da instituição. Use o botão de pesquisa para encontrar o endereço e a cidade.');
        });
    };

    const mensagemErro = nome => !!erro(nome) && <FormHelperText error>{erro(nome).message}</FormHelperText>;

    const campoTexto = (nome, rotulo, {sm = 6, disabled = false, inputComponent, placeholder, helperText, endAdornment, aoSair} = {}) => (
        <Grid item xs={12} sm={sm}>
            <LabelValue label={rotulo}>
                <FormControl variant='outlined' fullWidth error={!!erro(nome)} disabled={disabled}>
                    <Controller
                        name={nome}
                        control={control}
                        render={({field}) => (
                            <OutlinedInput
                                name={field.name}
                                value={field.value ?? ''}
                                onChange={field.onChange}
                                onBlur={evento => {
                                    field.onBlur();
                                    aoSair && aoSair(evento.target.value);
                                }}
                                inputRef={field.ref}
                                inputComponent={inputComponent}
                                placeholder={placeholder}
                                endAdornment={endAdornment}/>
                        )}/>
                    {mensagemErro(nome)}
                    {!erro(nome) && helperText && <FormHelperText>{helperText}</FormHelperText>}
                </FormControl>
            </LabelValue>
        </Grid>
    );

    const campoData = (nome, rotulo, {sm = 6, disabled = false} = {}) => (
        <Grid item xs={12} sm={sm}>
            <LabelValue label={rotulo}>
                <Controller
                    name={nome}
                    control={control}
                    render={({field}) => (
                        <KeyboardDatePicker
                            value={dataParaPicker(field.value)}
                            onChange={(data, texto) => field.onChange(data && data.isValid() ? data.format(FORMATO_DATA) : (texto || ''))}
                            onBlur={field.onBlur}
                            format={FORMATO_DATA}
                            placeholder='dd/mm/aaaa'
                            mask='__/__/____'
                            cancelLabel='Cancelar'
                            okLabel='OK'
                            invalidDateMessage='Data inválida'
                            inputVariant='outlined'
                            fullWidth
                            disabled={disabled}
                            error={!!erro(nome)}
                            helperText={erro(nome)?.message}/>
                    )}/>
            </LabelValue>
        </Grid>
    );

    const campoSelect = (nome, rotulo, opcoes, {sm = 4, disabled = false} = {}) => (
        <Grid item xs={12} sm={sm}>
            <LabelValue label={rotulo}>
                <FormControl variant='outlined' fullWidth error={!!erro(nome)} disabled={disabled}>
                    <Controller
                        name={nome}
                        control={control}
                        render={({field}) => (
                            <Select {...field} value={field.value ?? ''} displayEmpty>
                                <MenuItem value=''><em>Selecione...</em></MenuItem>
                                {opcoes.map(opcao => (
                                    <MenuItem key={opcao.valor} value={opcao.valor} disabled={!!opcao.desabilitado}>{opcao.descricao}</MenuItem>
                                ))}
                            </Select>
                        )}/>
                    {mensagemErro(nome)}
                </FormControl>
            </LabelValue>
        </Grid>
    );

    const opcoesDe = lista => lista.map(item => ({valor: String(item.id), descricao: item.descricao}));

    const campoCidade = (prefixo, sm = 5) => (
        <>
            <Grid item xs={12} sm={sm}>
                <LabelValue label='Cidade *'>
                    <FormControl variant='outlined' fullWidth error={!!erro(`${prefixo}.cidade`)}>
                        <Controller
                            name={`${prefixo}.cidade`}
                            control={control}
                            render={({field}) => (
                                <OutlinedInput value={field.value?.nome || ''} placeholder='Pesquise pelo CEP' readOnly/>
                            )}/>
                        {mensagemErro(`${prefixo}.cidade`)}
                    </FormControl>
                </LabelValue>
            </Grid>
            <Grid item xs={12} sm={2}>
                <LabelValue label='UF *'>
                    <FormControl variant='outlined' fullWidth>
                        <Controller
                            name={`${prefixo}.cidade`}
                            control={control}
                            render={({field}) => <OutlinedInput value={field.value?.uf?.uf || ''} readOnly/>}/>
                    </FormControl>
                </LabelValue>
            </Grid>
        </>
    );

    /* CEP, logradouro, número, complemento, bairro, cidade e UF, nas larguras do endereço público. */
    const blocoEndereco = (prefixo, obrigatorio) => {
        const marca = obrigatorio ? ' *' : '';
        return (
            <>
                {campoTexto(`${prefixo}.cep`, 'CEP *', {
                    sm: 4,
                    inputComponent: CepNumberFormatCustom,
                    placeholder: '00000-000',
                    aoSair: buscarCep(prefixo),
                    endAdornment: (
                        <InputAdornment position='end'>
                            <IconButton aria-label='Pesquisar endereço' title='Pesquisar endereço' edge='end'
                                        onClick={() => setAlvoPesquisa(prefixo)}>
                                <SearchOutlined/>
                            </IconButton>
                        </InputAdornment>
                    )
                })}
                {campoTexto(`${prefixo}.logradouro`, `Logradouro${marca}`, {sm: 8})}
                {campoTexto(`${prefixo}.numero`, `Número${marca}`, {sm: 4})}
                {campoTexto(`${prefixo}.complemento`, 'Complemento', {sm: 8})}
                {campoTexto(`${prefixo}.bairro`, `Bairro${marca}`, {sm: 5})}
                {campoCidade(prefixo)}
            </>
        );
    };

    const titulo = texto => (
        <Grid item xs={12}>
            <h4 className={classes.sectionTitle}>{texto}</h4>
        </Grid>
    );

    const secoes = [
        {
            id: 'dadosPessoais',
            label: 'Dados Pessoais',
            content: (
                <Grid container spacing={3}>
                    {titulo('Dados Pessoais')}
                    {campoTexto('pessoa.cpf', 'CPF *', {
                        sm: 4,
                        disabled: cpfTravado,
                        inputComponent: CpfNumberFormatCustom,
                        helperText: !cpfResolvido ? 'Informe o CPF para buscar o cadastro da pessoa.' : undefined
                    })}
                    {cpfResolvido && (
                        <>
                            {campoData('pessoa.dataNascimento', 'Data de Nascimento *', {sm: 4, disabled: dataNascimentoTravada})}
                            {campoSelect('pessoa.sexo', 'Sexo', SEXOS)}
                            {campoTexto('pessoa.nome', 'Nome Completo *', {disabled: nomeTravado})}
                            <Grid item xs={12} sm={6}>
                                <LabelValue label={habilitarNomeSocial ? 'Nome Social *' : 'Nome Social'}>
                                    <div className={classes.linhaNomeSocial}>
                                        <FormControl variant='outlined' className={classes.campoNomeSocial} error={!!erro('pessoa.nomeSocial')}
                                                     disabled={!habilitarNomeSocial}>
                                            <Controller
                                                name='pessoa.nomeSocial'
                                                control={control}
                                                render={({field}) => (
                                                    <OutlinedInput name={field.name} value={field.value ?? ''} onChange={field.onChange}
                                                                   onBlur={field.onBlur} inputRef={field.ref}/>
                                                )}/>
                                            {mensagemErro('pessoa.nomeSocial')}
                                        </FormControl>
                                        <Controller
                                            name='pessoa.habilitarNomeSocial'
                                            control={control}
                                            render={({field}) => (
                                                <FormControlLabel
                                                    className={classes.habilitarNomeSocial}
                                                    label='Habilitar'
                                                    control={<Checkbox size='small' color='primary' checked={!!field.value}
                                                                       onChange={evento => field.onChange(evento.target.checked)}/>}/>
                                            )}/>
                                    </div>
                                </LabelValue>
                            </Grid>
                            {campoTexto('pessoa.emailAtual', 'E-mail *', {sm: 4})}
                            {campoTexto('pessoa.celular', '1º Celular', {sm: 4, inputComponent: CelularNumberFormatCustom, placeholder: '(00) 00000-0000'})}
                            {campoTexto('pessoa.celularDois', '2º Celular', {sm: 4, inputComponent: CelularNumberFormatCustom, placeholder: '(00) 00000-0000'})}
                            {campoSelect('pessoa.estadoCivil', 'Estado Civil', opcoesDe(estadosCivis))}
                            {campoSelect('pessoa.raca', 'Raça', opcoesDe(racas))}
                            {campoSelect('pessoa.nacionalidade', 'Nacionalidade', opcoesDe(nacionalidades))}
                            {campoTexto('pessoa.nomeMae', 'Nome da Mãe', {sm: 4, disabled: nomeMaeTravado})}
                            {campoTexto('pessoa.nomePai', 'Nome do Pai', {sm: 4})}
                            {campoTexto('pessoa.telefone', 'Telefone Residencial', {sm: 4, inputComponent: TelefoneNumberFormatCustom, placeholder: '(00) 0000-0000'})}
                            {campoTexto('pessoa.rg', 'RG', {sm: 4})}
                            {campoTexto('pessoa.orgaoRg', 'Órgão Expedidor/UF', {sm: 4, placeholder: 'SSP/SP'})}
                            {!exibirResponsavel && dataEmancipacaoMaior && (
                                <Grid item xs={12} sm={4}>
                                    <LabelValue label='Data de Emancipação' value={dataEmancipacaoMaior}/>
                                </Grid>
                            )}
                            {ultimaAlteracao && (
                                <Grid item xs={12} sm={4}>
                                    <LabelValue label='Última alteração em' value={ultimaAlteracao}/>
                                </Grid>
                            )}
                            <Separator noCenterText/>
                            {titulo('Deficiências')}
                            <Grid item xs={12}>
                                <Controller
                                    name='pessoa.deficiencias'
                                    control={control}
                                    render={({field}) => {
                                        const selecionadas = field.value || [];
                                        const alternar = idDeficiencia => evento => field.onChange(evento.target.checked
                                            ? [...selecionadas, idDeficiencia]
                                            : selecionadas.filter(item => item !== idDeficiencia));
                                        return (
                                            <FormGroup row>
                                                {deficiencias.map(deficiencia => {
                                                    const idDeficiencia = String(deficiencia.id);
                                                    return (
                                                        <FormControlLabel
                                                            key={idDeficiencia}
                                                            control={<Checkbox color='primary' checked={selecionadas.includes(idDeficiencia)} onChange={alternar(idDeficiencia)}/>}
                                                            label={deficiencia.descricao}/>
                                                    );
                                                })}
                                            </FormGroup>
                                        );
                                    }}/>
                            </Grid>
                        </>
                    )}
                </Grid>
            )
        },
        {
            id: 'enderecoResidencial',
            label: 'Endereço Residencial',
            visible: cpfResolvido,
            content: (
                <Grid container spacing={3}>
                    {titulo('Endereço Residencial')}
                    {blocoEndereco('endereco', false)}
                </Grid>
            )
        },
        {
            id: 'responsavelLegal',
            label: 'Responsável Legal',
            visible: cpfResolvido && exibirResponsavel,
            content: (
                <Grid container spacing={3}>
                    {titulo('Responsável Legal')}
                    {campoSelect('tipoResponsavel', 'Tipo de Responsável Legal *',
                        TIPOS_RESPONSAVEL.map(tipo => ({...tipo, desabilitado: tipo.valor === 'NAO_TEM' && menorDeIdade})), {sm: 6})}
                    {tipoResponsavel === 'EMANCIPADO' && campoData('pessoa.dataEmancipacao', 'Data de Emancipação *')}
                    {comResponsavel && (
                        <>
                            {campoTexto('responsavel.nome', 'Nome do Responsável *')}
                            {campoTexto('responsavel.cpf', 'CPF do Responsável *', {sm: 4, inputComponent: CpfNumberFormatCustom})}
                            {campoTexto('responsavel.rg', 'RG do Responsável *', {sm: 4})}
                            {campoData('responsavel.dataNascimento', 'Data de Nascimento do Responsável *', {sm: 4})}
                            {campoTexto('responsavel.emailAtual', 'E-mail do Responsável *')}
                            {campoTexto('responsavel.celular', 'Celular do Responsável', {inputComponent: CelularNumberFormatCustom, placeholder: '(00) 00000-0000'})}
                            <Separator noCenterText/>
                            {titulo('Endereço do Responsável Legal')}
                            {blocoEndereco('responsavel.endereco', true)}
                        </>
                    )}
                </Grid>
            )
        }
    ].map(secao => ({...secao, hasError: secoesComErro.has(secao.id)}));

    useEffect(() => {
        if (!secaoFoco) return;
        const indice = indiceSecaoVisivel(secoes, secaoFoco.secao);
        if (indice >= 0 && tabRef.current) {
            tabRef.current.setTabIndex(indice);
        }
    }, [secaoFoco]);

    return (
        <MuiPickersUtilsProvider utils={MomentUtils} locale='pt-br'>
            <GenericTab
                ref={tabRef}
                orientation='vertical'
                indicatorColor='primary'
                textColor='primary'
                scrollButtons='auto'
                noAppBar
                className={classes.tabs}
                content={secoes}/>
            <DialogBuscarEndereco
                open={!!alvoPesquisa}
                onClose={() => setAlvoPesquisa(null)}
                onSelect={cep => alvoPesquisa && preencherEndereco(alvoPesquisa, cep)}/>
        </MuiPickersUtilsProvider>
    );
}

EtapaDadosPessoais.propTypes = {
    control: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    watch: PropTypes.func.isRequired,
    setValue: PropTypes.func.isRequired,
    secoesComErro: PropTypes.instanceOf(Set).isRequired,
    secaoFoco: PropTypes.object,
    cpfTravado: PropTypes.bool,
    cpfResolvido: PropTypes.bool,
    nomeTravado: PropTypes.bool,
    dataNascimentoTravada: PropTypes.bool,
    nomeMaeTravado: PropTypes.bool,
    exibirResponsavel: PropTypes.bool,
    menorDeIdade: PropTypes.bool,
    dataEmancipacaoMaior: PropTypes.string,
    ultimaAlteracao: PropTypes.string,
    estadosCivis: PropTypes.array,
    racas: PropTypes.array,
    nacionalidades: PropTypes.array,
    deficiencias: PropTypes.array
};
